"use client";

import { useEffect, useState } from "react";

import type { Route } from "next";
import { useRouter } from "next/navigation";

import { useEnquiryModalView } from "@/app/_libs/utils/my-enquiries/use-enquiry-modal-view";
import { cn } from "@/lib/utils";

import { DynamicModalWrapper } from "../../shared/dynamic-modal-wrapper";
import {
  EnquiryModalDropdownContext,
  type EnquirySuccessConfig,
} from "./enquiry-modal-dropdown-context";
import { enquiryModalMap } from "./enquiry-modal-map";
import type { AddEnquiryPickerType } from "./enquiry-types";
import { PrivateInspectionForm } from "./forms/private-inspection-form";
import { SuccessModal } from "./success-modal";

// ----- Card only (no modal) -----
interface EnquiryCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  onClick: () => void;
}

const EnquiryCard = ({ icon, title, subtitle, onClick }: EnquiryCardProps) => {
  return (
    <>
      <button
        onClick={onClick}
        className={cn(
          "flex flex-col items-center justify-center gap-3",
          "rounded-lg bg-white p-2",
          "cursor-pointer",
          "w-full",
          "border-secondary-100 border",
        )}>
        {/* Icon with circular background */}
        <div className="bg-primary-100 flex h-16 w-16 items-center justify-center rounded-full">
          <div className="flex items-center justify-center">{icon}</div>
        </div>

        {/* Title */}
        <h3 className="text-typography-900 text-center text-base leading-tight font-semibold">
          {title}
        </h3>

        {/* Subtitle */}
        {subtitle != null && (
          <p className="text-typography-500 text-center text-sm leading-tight">{subtitle}</p>
        )}
      </button>
    </>
  );
};

export interface EnquiryModalShellCloseOptions {
  redirecting?: boolean;
}

export interface EnquiryModalShellProps {
  open: boolean;
  onOpenChange: (open: boolean, options?: EnquiryModalShellCloseOptions) => void;
  selectedType: AddEnquiryPickerType | null;
  preselectedListingId?: string | null;
  preselectedListingState?: string | null;
}

export const EnquiryModalShell = ({
  open,
  onOpenChange,
  selectedType,
  preselectedListingId,
  preselectedListingState,
}: EnquiryModalShellProps) => {
  const router = useRouter();
  const { modalView, setModalView } = useEnquiryModalView("book-inspection");
  const [propertyDropdownExpanded, setPropertyDropdownExpanded] = useState(false);
  const [dateTimeVisible, setDateTimeVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successConfig, setSuccessConfig] = useState<EnquirySuccessConfig | null>(null);

  const isBookInspection = selectedType === "BOOK_INSPECTION";
  const showPrivateInspection = isBookInspection && modalView === "private-inspection";

  const setPropertyDropdownOpen = (open: boolean, suggestionCount?: number) => {
    setPropertyDropdownExpanded(open && (suggestionCount ?? 0) > 0);
  };

  useEffect(() => {
    setDateTimeVisible(false);
  }, [selectedType, showPrivateInspection]);

  useEffect(() => {
    if (open) {
      setModalView("book-inspection");
      setSuccessConfig(null);
    } else {
      setIsSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    setIsSubmitting(false);
    setSuccessConfig(null);
  }, [selectedType, showPrivateInspection]);

  if (selectedType == null) return null;

  const config = enquiryModalMap[selectedType];
  if (!config) return null;

  const FormComponent = config.FormComponent;
  const activeFormId = showPrivateInspection ? "private-inspection-form" : config.formId;
  const modalTitle = showPrivateInspection
    ? "Book Private Inspection"
    : isBookInspection
      ? "Book Public Inspection"
      : config.title;

  const contextValue = {
    setPropertyDropdownOpen,
    setDateTimeVisible,
    isSubmitting,
    setIsSubmitting,
    successConfig,
    setSuccessConfig,
    preselectedListingId: preselectedListingId ?? null,
    preselectedListingState: preselectedListingState ?? null,
  };

  const handlePrimaryButtonClick = () => {
    const redirect = successConfig?.primaryButtonRedirect ?? "close";
    const isRedirecting = redirect === "my-enquiries" || redirect === "dashboard";
    onOpenChange(false, { redirecting: isRedirecting });
    if (redirect === "my-enquiries") {
      router.push("/my-enquiries");
    } else if (redirect === "dashboard") {
      router.push("/dashboard");
    }
  };

  const handleBackButtonClick = () => {
    onOpenChange(false, { redirecting: false });
  };

  return (
    <DynamicModalWrapper
      open={open}
      onOpenChange={(open) => onOpenChange(open)}
      title={successConfig != null ? "" : modalTitle}
      dismissible={true}
      closeOnOverlayClick={true}
      ensureFooterVisible={true}
      footerClassName="!border-t-0"
      childrenClassName={
        propertyDropdownExpanded && !dateTimeVisible ? "!px-4 !py-2 !pb-25" : "!px-4 !py-2"
      }
      footer={
        successConfig != null ? undefined : (
          <>
            <div className="flex h-11 items-center gap-3">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="border-border text-typography-800 inline-flex h-full min-w-[96px] cursor-pointer items-center justify-center rounded-sm border px-4 py-2 text-sm font-medium hover:bg-white">
                Cancel
              </button>
              <button
                type="submit"
                {...(activeFormId != null && { form: activeFormId })}
                disabled={isSubmitting}
                className="bg-primary-700 text-primary-0 hover:bg-primary-700 inline-flex h-full min-w-[140px] flex-1 cursor-pointer items-center justify-center rounded-sm px-6 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-70">
                {isSubmitting ? "Submitting" : "Submit"}
              </button>
            </div>

            {/* Helper text – only when showing public book inspection */}
            {isBookInspection && !showPrivateInspection && (
              <div className="text-secondary-700 p-4 pt-4 text-center text-sm">
                Didn’t Find Your Preferred Slot?{" "}
                <button
                  type="button"
                  onClick={() => setModalView("private-inspection")}
                  className="text-medium text-primary-700 cursor-pointer font-medium underline-offset-2">
                  Book Private Inspection
                </button>
              </div>
            )}

            {/* Helper text – only when showing private inspection (switch back to public) */}
            {isBookInspection && showPrivateInspection && (
              <div className="text-secondary-700 p-4 pt-4 text-center text-sm">
                Prefer a public inspection?{" "}
                <button
                  type="button"
                  onClick={() => setModalView("book-inspection")}
                  className="text-medium text-primary-700 cursor-pointer font-medium underline-offset-2">
                  Book Public Inspection
                </button>
              </div>
            )}
          </>
        )
      }>
      <EnquiryModalDropdownContext.Provider value={contextValue}>
        <div className="space-y-5">
          {successConfig != null ? (
            <SuccessModal
              title={successConfig.title}
              subheading={successConfig.subheading}
              label={successConfig.primaryButtonLabel ?? "Back to Home"}
              onBackToHome={handlePrimaryButtonClick}
              extraButton={successConfig.extraButton}
              extraButtonLabel={successConfig.extraButtonLabel}
              extraButtonOnClick={successConfig.extraButton ? handleBackButtonClick : undefined}
            />
          ) : showPrivateInspection ? (
            <PrivateInspectionForm />
          ) : FormComponent != null ? (
            <FormComponent />
          ) : null}
        </div>
      </EnquiryModalDropdownContext.Provider>
    </DynamicModalWrapper>
  );
};

export interface AddEnquiryModalProps {
  /** When provided, selecting an enquiry type will call this instead of opening the form modal internally (so parent can close this modal and open form modal alone). */
  onSelectEnquiryType?: (type: AddEnquiryPickerType) => void;
}

export const AddEnquiryModal = ({ onSelectEnquiryType }: AddEnquiryModalProps = {}) => {
  const router = useRouter();
  const [isBookInspectionOpen, setIsBookInspectionOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<AddEnquiryPickerType | null>(null);

  const openModal = (type: AddEnquiryPickerType) => {
    const config = enquiryModalMap[type];
    if (config?.redirectTo) {
      router.push(config.redirectTo as Route);
      return;
    }

    if (onSelectEnquiryType) {
      onSelectEnquiryType(type);
      return;
    }
    setSelectedType(type);
    setIsBookInspectionOpen(true);
  };

  const closeModal = () => {
    setIsBookInspectionOpen(false);
    setSelectedType(null);
  };

  const enquiryTypes = Object.keys(enquiryModalMap) as AddEnquiryPickerType[];

  return (
    <div className="flex h-full w-full flex-col gap-6">
      {/* Cards Grid */}
      <div className="grid grid-cols-2 gap-4">
        {enquiryTypes.map((type) => {
          const config = enquiryModalMap[type];
          return (
            <EnquiryCard
              key={type}
              icon={config.icon}
              title={config.title}
              subtitle={config.subtitle}
              onClick={() => openModal(type)}
            />
          );
        })}
      </div>

      <EnquiryModalShell
        open={isBookInspectionOpen}
        onOpenChange={(open, _options?) => !open && closeModal()}
        selectedType={selectedType}
      />
    </div>
  );
};
